import type { ExamRef } from '../domain/examCatalogue';
import type { ServerClient } from './client';

/**
 * Der Katalog vom Server — `GET /rest/v1/exam/available`.
 *
 * ⚠️ **Das ist die Übersicht für Prüfungsgeräte**, nicht `/exam/list`. Sie trägt zu jeder
 * Prüfung Art, Datum, Zeiten, Änderungsstand und drei Aggregate (wie viele Items, wie viele
 * Medien, wie viele Bytes) — und sie ist **ungefiltert**, auch Unveröffentlichtes. Das ist eine
 * ausdrückliche Festlegung des Nutzers vom 2026-09-12: Startfenster und Ablauf wertet die
 * **App** aus, nicht der Server.
 *
 * ⬅️ Bis zum 2026-09-16 stand hier `/exam/list`, und darunter der Vermerk, Relay könne eine
 * Prüfung zwar auflisten, aber nicht herunterladen. Das gilt nicht mehr — s. `server/download.ts`.
 */

/** Die Felder, die uns von einer Zeile der Geräteübersicht interessieren. */
interface ServerExam {
  id?: string;
  name?: string;
  description?: string | null;
  startDate?: string | null;
  startTime?: string | null;
  published?: boolean;
  /** Simulationsprüfung: die App schickt für sie nichts hinaus (Relay, seit 2026-09-18). */
  simulative?: boolean;
  lastModifiedDate?: string | null;
  /** Ein Passwort schützt den Download (Relay `00426bb`). */
  passwordProtected?: boolean;
  /** OSCE, ORAL, SUMMATIVE … — Stage zeigt nur OSCE (Nutzer, 2026-11-01). */
  examType?: string | null;
}

interface ListResponse {
  exams?: ServerExam[];
  count?: number;
}

/** „20260911" + „0900" → „11.09.2026 · 09:00". Fehlt etwas, bleibt der Teil einfach weg. */
export function metaZeile(exam: ServerExam): string {
  const teile: string[] = [];
  const d = exam.startDate ?? '';
  if (/^\d{8}$/.test(d)) teile.push(d.slice(6, 8) + '.' + d.slice(4, 6) + '.' + d.slice(0, 4));
  const t = exam.startTime ?? '';
  if (/^\d{4}$/.test(t)) teile.push(t.slice(0, 2) + ':' + t.slice(2, 4));
  if (teile.length === 0 && exam.description) return exam.description;
  return teile.join(' · ');
}

/**
 * Die Prüfungen des Servers, in der Form, die `mergeCatalogue` erwartet.
 *
 * Einträge ohne `id` oder ohne `name` werden **weggelassen, nicht erfunden**: eine Prüfung ohne
 * Kennung liesse sich später nicht wiederfinden, und ein erfundener Name stünde im Katalog des
 * Prüfers, als wäre er echt.
 */
export async function holeKatalog(client: ServerClient, _limit = 200): Promise<ExamRef[]> {
  // `available` kennt keine Grenze: es ist die Übersicht **eines Geräts**, und die ist so lang,
  // wie das Haus Prüfungen führt. Der Parameter bleibt für die Aufrufer stehen.
  void _limit;
  const antwort = await client.get<ListResponse>('/rest/v1/exam/available');
  const exams = antwort?.exams ?? [];
  return exams
    .filter((e): e is ServerExam & { id: string; name: string } => Boolean(e?.id) && Boolean(e?.name))
    // Fehlt die Art, gilt OSCE — wie beim Einlesen des Dokuments (`dokument.ts`).
    .filter((e) => (e.examType ?? 'OSCE') === 'OSCE')
    // ⚠️ **`stand` gibt es jetzt.** Bis zum 2026-09-16 stand hier ausdrücklich `undefined`, weil
    // `/exam/list` kein Änderungsdatum führte — und damit trug keine Relay-Prüfung je die
    // Warnung „am Server geändert". `available` trägt `lastModifiedDate`, also vergleicht
    // `domain/examCatalogue.ts::istVeraltet` von nun an auch hier.
    .map((e) => ({
      id: e.id,
      name: e.name,
      meta: metaZeile(e),
      stand: /^\d{17}$/.test(e.lastModifiedDate ?? '') ? (e.lastModifiedDate as string) : undefined,
      // Schon hier, damit eine noch nicht geladene Simulationsprüfung in der Auswahl markiert
      // ist — nicht erst nach dem Herunterladen.
      simulativ: e.simulative === true,
      geschuetzt: e.passwordProtected === true,
    }));
}
